"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
const useMorphingText = (texts, isHovered, setIsMorphing) => {
  const $ = _c(11);
  const text1Ref = useRef(null);
  const text2Ref = useRef(null);
  const fractionRef = useRef(0);
  let t0;
  if ($[0] !== texts) {
    t0 = fraction => {
      const [current1, current2] = [text1Ref.current, text2Ref.current];
      if (!current1 || !current2) {
        return;
      }
      if (fraction === 0) {
        current2.style.filter = "blur(32px)";
        current2.style.opacity = "0%";
      } else {
        current2.style.filter = `blur(${Math.min(8 / fraction - 8, 32)}px)`;
        current2.style.opacity = `${Math.pow(fraction, 0.4) * 100}%`;
      }
      const invertedFraction = 1 - fraction;
      if (invertedFraction === 0) {
        current1.style.filter = "blur(32px)";
        current1.style.opacity = "0%";
      } else {
        current1.style.filter = `blur(${Math.min(8 / invertedFraction - 8, 32)}px)`;
        current1.style.opacity = `${Math.pow(invertedFraction, 0.4) * 100}%`;
      }
      current1.textContent = texts[0];
      current2.textContent = texts[1 % texts.length];
    };
    $[0] = texts;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const setStyles = t0;
  let t1;
  let t2;
  if ($[2] !== setStyles) {
    t1 = () => {
      setStyles(fractionRef.current);
    };
    t2 = [setStyles];
    $[2] = setStyles;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[5] !== isHovered || $[6] !== setIsMorphing || $[7] !== setStyles) {
    t3 = () => {
      let animationFrameId;
      let lastTime = performance.now();
      const animate = time => {
        const dt = (time - lastTime) / 1000;
        lastTime = time;
        const targetFraction = isHovered ? 1 : 0;
        if (fractionRef.current !== targetFraction) {
          setIsMorphing(true);
          if (targetFraction === 1) {
            fractionRef.current = Math.min(1, fractionRef.current + dt / 0.5);
          } else {
            fractionRef.current = Math.max(0, fractionRef.current - dt / 0.5);
          }
          setStyles(fractionRef.current);
          if (fractionRef.current === targetFraction) {
            setIsMorphing(false);
          } else {
            animationFrameId = requestAnimationFrame(animate);
          }
        } else {
          setIsMorphing(false);
        }
      };
      const targetFraction_0 = isHovered ? 1 : 0;
      if (fractionRef.current !== targetFraction_0) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        setIsMorphing(false);
      }
      return () => {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
      };
    };
    t4 = [isHovered, setStyles, setIsMorphing];
    $[5] = isHovered;
    $[6] = setIsMorphing;
    $[7] = setStyles;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      text1Ref,
      text2Ref
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
interface MorphingTextProps {
  className?: string;
  texts: string[];
  forceHover?: boolean;
}
const Texts: React.FC<Pick<MorphingTextProps, "texts"> & {
  isHovered: boolean;
  setIsMorphing: (val: boolean) => void;
}> = t0 => {
  const $ = _c(12);
  const {
    texts,
    isHovered,
    setIsMorphing
  } = t0;
  const {
    text1Ref,
    text2Ref
  } = useMorphingText(texts, isHovered, setIsMorphing);
  let t1;
  if ($[0] !== texts) {
    t1 = texts.reduce(_temp);
    $[0] = texts;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span className="invisible block">{t1}</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== text1Ref) {
    t3 = <span className="absolute inset-0 m-auto inline-flex items-center justify-center text-center" ref={text1Ref} />;
    $[4] = text1Ref;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== text2Ref) {
    t4 = <span className="absolute inset-0 m-auto inline-flex items-center justify-center text-center" ref={text2Ref} />;
    $[6] = text2Ref;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = <>{t2}{t3}{t4}</>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
const SvgFilters: React.FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg id="filters" className="fixed h-0 w-0" preserveAspectRatio="xMidYMid slice"><defs><filter id="threshold"><feColorMatrix in="SourceGraphic" type="matrix" values={"1 0 0 0 0\n                  0 1 0 0 0\n                  0 0 1 0 0\n                  0 0 0 255 -140"} /></filter></defs></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const MorphingText: React.FC<MorphingTextProps> = t0 => {
  const $ = _c(14);
  const {
    texts,
    className,
    forceHover: t1
  } = t0;
  const forceHover = t1 === undefined ? false : t1;
  const [isHovered, setIsHovered] = useState(false);
  const [isMorphing, setIsMorphing] = useState(false);
  const t2 = isMorphing ? "filter-[url(#threshold)_blur(0.6px)]" : "filter-none";
  let t3;
  if ($[0] !== className || $[1] !== t2) {
    t3 = cn("relative inline-block transition-all transform-gpu will-change-[filter]", t2, className);
    $[0] = className;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setIsHovered(true);
    t5 = () => setIsHovered(false);
    t6 = () => setIsHovered(true);
    t7 = () => setIsHovered(false);
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
  } else {
    t4 = $[3];
    t5 = $[4];
    t6 = $[5];
    t7 = $[6];
  }
  const t8 = isHovered || forceHover;
  let t9;
  if ($[7] !== t8 || $[8] !== texts) {
    t9 = <Texts texts={texts} isHovered={t8} setIsMorphing={setIsMorphing} />;
    $[7] = t8;
    $[8] = texts;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <SvgFilters />;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== t3 || $[12] !== t9) {
    t11 = <div className={t3} onMouseEnter={t4} onMouseLeave={t5} onTouchStart={t6} onTouchEnd={t7}>{t9}{t10}</div>;
    $[11] = t3;
    $[12] = t9;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  return t11;
};
function _temp(a, b) {
  return a.length > b.length ? a : b;
}